/**
 * A recording on its way to the server (UC-8, light). It is held in memory from the moment the
 * recorder ends — never at rest on the phone (ADR-0048) — with the id it got then: every send
 * of it, the first and each *Try again*, names the same capture, so the server never stores it
 * twice. Its hash is computed once: a retry sends the same bytes. Pure but for the clock and the
 * id it is given; the hook `useRecordingUpload` owns the requests.
 */
import type { HeldRecordingSummary } from '@/components/humint/questioning/recording-types'
import type { CaptureUploadOptions } from '@/services/api/humint'
import type { RecordingExtension, RecordingUpload } from '@/services/api/humint-recordings'
import { randomUuid } from '../mappers/enrol'
import { classifyRecordingSendError, type RecordingSendFailure } from './recording-errors'

/** What ended up recorded, or the file picked instead. */
export interface RecordingSource {
  blob: Blob
  extension: RecordingExtension
  /** When the recording started; for a picked file, when it was picked. ISO. */
  capturedAt: string
  /** Recorded time; null for a picked file. */
  durationMs: number | null
  /** The microphone stopped on its own, or the recording reached the size limit. */
  interrupted: boolean
}

/**
 * Where the recording goes: the languages of its transcript, the encounter it joins and who else
 * took part in it.
 */
export interface RecordingRequest {
  /** The Whisper codes of the languages spoken, in the order chosen; none leaves one to detection. */
  languages: readonly string[]
  /** The encounter the recording joins (`enc-<uuid>`); null for none. */
  encounterRef: string | null
  /** The other subjects who took part, without the recording's holder; none without an encounter. */
  participantRefs: readonly string[]
}

/** A recording waiting to be stored; `sha256` once it was computed. */
export interface HeldRecording extends RecordingSource, RecordingRequest {
  /** The capture's id, also its idempotency key: every send repeats it. */
  readonly captureId: string
  sha256: string | null
}

/** Take a recording to be sent, under a fresh capture id that stays with it. */
export function holdRecording(
  source: RecordingSource,
  request: RecordingRequest,
  newId: () => string = randomUuid
): HeldRecording {
  return { ...source, ...request, captureId: newId(), sha256: null }
}

/** What the saving screen shows of a held recording. */
export function heldSummary(held: HeldRecording): HeldRecordingSummary {
  return { durationMs: held.durationMs, sizeBytes: held.blob.size, interrupted: held.interrupted }
}

/** What sending needs from outside: the hash, the request and whether there is a connection. */
export interface SendDeps {
  hash: (blob: Blob) => Promise<string>
  upload: (upload: RecordingUpload, options: CaptureUploadOptions) => Promise<unknown>
  online: () => boolean
}

export interface SendHooks extends CaptureUploadOptions {
  /** The hash is known; the bytes start to go. */
  onUploading?: () => void
}

export type SendOutcome = { kind: 'saved'; captureId: string } | RecordingSendFailure

/** The held recording as the request the capture endpoint takes. */
function uploadOf(held: HeldRecording, sha256: string): RecordingUpload {
  return {
    captureId: held.captureId,
    blob: held.blob,
    sha256,
    extension: held.extension,
    capturedAt: held.capturedAt,
    languages: held.languages,
    sameEncounterRef: held.encounterRef,
    // The server takes participants only with the encounter they are subjects of.
    participantRefs: held.encounterRef ? held.participantRefs : [],
  }
}

/**
 * Hash the recording (the first time only) and send it. Resolves to how it went — saved,
 * cancelled by the page, or failed with why and whether sending it again may help — and never
 * rejects.
 */
export async function sendRecording(
  held: HeldRecording,
  deps: SendDeps,
  hooks: SendHooks = {}
): Promise<SendOutcome> {
  try {
    held.sha256 ??= await deps.hash(held.blob)
    if (hooks.signal?.aborted) return { kind: 'cancelled' }
    hooks.onUploading?.()
    await deps.upload(uploadOf(held, held.sha256), {
      signal: hooks.signal,
      onProgress: hooks.onProgress,
    })
    return { kind: 'saved', captureId: held.captureId }
  } catch (error) {
    return classifyRecordingSendError(error, deps.online())
  }
}
